import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import API from "../../../utils/helpers/APIHelper";
import { RootState } from "../../store";
import ErrorHandler from "../../../utils/helpers/ErrorHandler";
import { ENDPOINTS } from "../../../API/apiConstants";

// ── Types ────────────────────────────────────────────────

export interface ExperiencePrice {
  audience_type: string;
  label: string;
  currency: string;
  regular_price: string | null;
  early_bird_price: string | null;
  early_bird_deadline: string | null;
  active_price: string | null;
  has_early_bird: boolean;
}

export interface ExperienceVideo {
  url: string;
  title: string | null;
}

export interface ExperienceSummary {
  id: number;
  slug: string;
  title: string;
  location: string;
  description: string;
  duration: string;
  group_size: string;
  difficulty: string;
  category: string;
  currency: string;
  rating: number;
  review_count: number;
  // Legacy single-price fields (kept for backwards compat)
  early_bird_price: string | null;
  early_bird_deadline: string | null;
  regular_price: string | null;
  // New structured pricing
  prices?: ExperiencePrice[];
  from_price?: string | null;
  from_currency?: string | null;
  next_dates: string[];
  cover_image: string | null;
}

export interface ExperienceDetail extends ExperienceSummary {
  long_description: string;
  travel_style: string;
  travel_style_detail: string;
  accommodation: string;
  accommodation_detail: string;
  physical_rating: string;
  physical_rating_detail: string;
  trip_type: string;
  trip_type_detail: string;
  age_requirement: string;
  age_requirement_detail: string;
  important_notes?: string[];
  itinerary: {
    day: number;
    title: string;
    activities: string[];
  }[];
  included: string[];
  not_included: string[];
  what_to_bring: string[];
  images: string[];
  // Legacy single video (kept for backwards compat)
  video_url?: string | null;
  // New multi-video array
  videos?: ExperienceVideo[];
}

interface ExperiencesState {
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  experiences: ExperienceSummary[];
  experienceDetail: ExperienceDetail | null;
}

// ── Initial State ────────────────────────────────────────

const initialState: ExperiencesState = {
  loading: false,
  detailLoading: false,
  error: null,
  experiences: [],
  experienceDetail: null,
};

// ── Thunks ───────────────────────────────────────────────

export const getAllExperiences = createAsyncThunk<
  any,
  { category?: string } | void,
  { state: RootState; rejectValue: any }
>("experiences/getAll", async (params, { rejectWithValue }) => {
  try {
    const query = params?.category ? `?category=${params.category}` : "";
    const response = await API.get(`${ENDPOINTS.GET_ALL_EXPERIENCES}${query}`);
    return response;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const getExperienceDetail = createAsyncThunk<
  any,
  string,
  { state: RootState; rejectValue: any }
>("experiences/getDetail", async (slug, { rejectWithValue }) => {
  try {
    const response = await API.get(ENDPOINTS.GET_EXPERIENCE_DETAIL(slug));
    return response;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

// ── Slice ────────────────────────────────────────────────

const experiencesSlice = createSlice({
  name: "experiences",
  initialState,
  reducers: {
    clearExperienceDetail: (state) => {
      state.experienceDetail = null;
    },
  },
  extraReducers: (builder) => {
    // Get all
    builder
      .addCase(getAllExperiences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllExperiences.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.experiences = action.payload?.experiences || action.payload || [];
      })
      .addCase(getAllExperiences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load experiences";
        state.experiences = [];
      });

    // Get detail
    builder
      .addCase(getExperienceDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getExperienceDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.detailLoading = false;
        state.experienceDetail = action.payload?.experience || action.payload || null;
      })
      .addCase(getExperienceDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload?.message || "Failed to load experience";
        state.experienceDetail = null;
      });
  },
});

export const { clearExperienceDetail } = experiencesSlice.actions;
export default experiencesSlice.reducer;